import { useTranslation } from 'react-i18next'
import { DashboardCleaningReportField, DashboardPhase, DashboardReceptionReportField, MealsSortModel } from 'src/app/pages/common/dashboard/dashboard.types'
import { useAuth } from '../auth/auth.hook'
import { CleaningState, FiscalType } from 'src/api/api.generated'
import { serializeDate } from 'src/api/apollo-client/hic-date-resolver'

type AccountBalanceParams =
  | {
      type: ReportType.ACCOUNT_BALANCE
      body: {
        tenantId: string
        propertyId: string
        bills: {
          billId: string
          productionAssignedIds?: string[]
        }[]
        depositIds?: string[]
      }
    }
  | {
      type: ReportType.MEALS
      body: {
        tenantId: string
        propertyId: string
        day: string
        filters?: {
          name?: string
          group?: string
          phases?: DashboardPhase[]
        }
        sort: MealsSortModel
      }
    }
  | {
      type: ReportType.RECEPTION
      body: {
        tenantId: string
        propertyId: string
        day: string
        filters?: {
          name?: string
          group?: string
          phases?: DashboardPhase[]
        }
        sort?: {
          field: DashboardReceptionReportField
          dir?: 'asc' | 'desc'
        }
      }
    }
  | {
      type: ReportType.DOCUMENT
      body: {
        tenantId: string
        propertyId: string
        documentId: string
      }
    }
  | {
      type: ReportType.DISTINTA_DI_CASSA
      body: {
        tenantId: string
        propertyId: string
        from: string
        to: string
        filters?: {
          fiscalType?: FiscalType[]
          customerId?: string
          cashDeskTypeId?: string
          cashDeskId?: string
          draftOnly?: boolean
          userTsId?: string
        }
      }
    }
  | {
      type: ReportType.STATISTICHE
      body: {
        tenantId: string
        propertyId: string
        currentPeriod: {
          start: Date
          end: Date
        }
        comparePeriod: {
          start: Date
          end: Date
        }
      }
    }
  | {
      type: ReportType.CORRISPETTIVI_E_INCASSI
      body: {
        tenantId: string
        propertyId: string
        start: string
        end: string
        filters?: {
          cashDeskTypeId?: string
          cashDeskId?: string
        }
      }
    }
  | {
      type: ReportType.DETAILED_CITY_TAX
      body: {
        tenantId: string
        propertyId: string
        start: string
        end: string
        sourceDate: 'COMPETENCE' | 'CHECKOUT'
        filters?: {
          resourceTypeIds?: string[]
          resourceIds?: string[]
        }
      }
    }
  | {
      type: ReportType.HOUSEKEEPER
      body: {
        tenantId: string
        propertyId: string
        day: string
        filters?: {
          cleaningState?: CleaningState
          positionIds?: string[]
        }
        sort?: {
          field: DashboardCleaningReportField
          dir?: 'asc' | 'desc'
        }
      }
    }
  | {
      type: ReportType.HOUSEKEEPER_PER_PERIOD
      body: {
        tenantId: string
        propertyId: string
        start: string
        end: string
      }
    }
  | {
      type: ReportType.MEALS_BY_DAY
      body: {
        tenantId: string
        propertyId: string
        from: string
        to: string
      }
    }
  | {
      type: ReportType.ACCOUNT_BALANCE_PARTITARI
      body: {
        tenantId: string
        propertyId: string
        start: string
        end: string
        acctgEntryType: 'OPEN' | 'CLOSE' | 'ALL'
        filters?: {
          cashDeskTypeId?: string
          customerId?: string
          fiscalType?: 'RECEIPT' | 'INVOICE' | 'CREDIT_NOTE' | 'PAID_IN_FULL'
          paymentActiveId?: string
        }
      }
    }
  | {
      type: ReportType.ANTICIPI_RESIDUI
      body: {
        tenantId: string
        propertyId: string
        start: string
        end: string
        filters?: {
          amountUsed?: 'PARTIAL' | 'TOTAL'
          advanceType?: 'FISCAL' | 'NON_FISCAL'
          cashDeskTypeId?: string
          customerId?: string
        }
      }
    }

type Report = {
  file: string
}

type ReportError = {
  message: string
}

export function isReport(value: Report | ReportError | null | undefined): value is Report {
  if (value === null || value === undefined) {
    return false
  }
  return Object.keys(value).includes('file')
}

export function isReportError(value: Report | ReportError | null | undefined): value is ReportError {
  if (value === null || value === undefined) {
    return false
  }
  return !isReport(value)
}

export enum ReportType {
  ACCOUNT_BALANCE = '/reports/contabilita/estrattoconto',
  ACCOUNT_BALANCE_PARTITARI = '/reports/contabilita/estrattocontopartitari',
  MEALS = '/reports/dashboard/meals',
  MEALS_BY_DAY = '/reports/dashboard/mealsbyday',
  RECEPTION = '/reports/dashboard/reception',
  DOCUMENT = '/reports/documenti/documento',
  DISTINTA_DI_CASSA = '/reports/documenti/distintadicassa',
  STATISTICHE = '/reports/statistiche/statistiche',
  CORRISPETTIVI_E_INCASSI = '/reports/documenti/corrispettivi',
  DETAILED_CITY_TAX = '/reports/citytax/dettagliocitytax',
  HOUSEKEEPER = '/reports/dashboard/housekeeper/housekeeper',
  HOUSEKEEPER_PER_PERIOD = '/reports/dashboard/housekeeper/housekeeperperiod',
  ANTICIPI_RESIDUI = '/reports/contabilita/anticipiresidui',
}

/***
 * Dates are serialized using same serialization function used in Apollo Client.
 */
const customDateSerializer = function (this: any, key: string, value: any) {
  return this[key] instanceof Date //
    ? serializeDate(this[key])
    : this[key]
}

export default function useReportGenerator() {
  const { t } = useTranslation()
  const { auth } = useAuth()

  const generateReport = async (params: AccountBalanceParams): Promise<Report | ReportError> => {
    if (!auth.loading && auth.isAuthenticated) {
      try {
        const response = await fetch(`${process.env.REACT_APP_END_POINT}${params.type}`, {
          method: 'POST',
          body: JSON.stringify(params.body, customDateSerializer),
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${auth.token}`,
          },
        })

        if (!response.ok) {
          throw new Error(`Error! status: ${response.status}`)
        }

        const result = await response.blob()
        const file = window.URL.createObjectURL(result)

        return { file: file }
      } catch (error) {
        if (error instanceof Error) {
          return { message: error.message }
        } else {
          return { message: t('pms.components.report_viewer.request_error') }
        }
      }
    } else {
      return { message: t('pms.components.report_viewer.missing_token_error') }
    }
  }

  return generateReport
}
